import { Dominio } from '../value-objects/Dominio';
import { EstadoSensor } from '../value-objects/EstadoSensor';
import type { Lectura } from '../value-objects/Lectura';
import type { Umbral } from './Umbral';

export interface SensorProps {
  id: number;
  nombre: string;
  esDigital: boolean;
  unidad: string | null;
  simbolo: string | null;
  tipoNombre: string | null;
  codigoEnvio: string | null;
  activo: boolean;
  dominio: Dominio;
  nodoId: number | null;
  nodoNombre: string | null;
  areaId: number | null;
  areaNombre: string | null;
  clienteId: number | null;
  clienteNombre: string | null;
  decimales: number;
  lectura: Lectura | null;
  umbral: Umbral | null;
}

/**
 * Un sensor con su última lectura y su umbral.
 *
 * Es inmutable: las actualizaciones que llegan por tiempo real producen una
 * instancia nueva (`conLectura`), lo que permite que React detecte el cambio
 * por identidad sin comparaciones profundas.
 */
export class Sensor {
  constructor(private readonly props: SensorProps) {}

  get id(): number {
    return this.props.id;
  }
  get nombre(): string {
    return this.props.nombre;
  }
  get esDigital(): boolean {
    return this.props.esDigital;
  }
  get esAnalogico(): boolean {
    return !this.props.esDigital;
  }
  get unidad(): string | null {
    return this.props.unidad;
  }
  get simbolo(): string | null {
    return this.props.simbolo;
  }
  get tipoNombre(): string | null {
    return this.props.tipoNombre;
  }
  get codigoEnvio(): string | null {
    return this.props.codigoEnvio;
  }
  get activo(): boolean {
    return this.props.activo;
  }
  get dominio(): Dominio {
    return this.props.dominio;
  }
  get nodoId(): number | null {
    return this.props.nodoId;
  }
  get nodoNombre(): string | null {
    return this.props.nodoNombre;
  }
  get areaId(): number | null {
    return this.props.areaId;
  }
  get areaNombre(): string | null {
    return this.props.areaNombre;
  }
  get clienteId(): number | null {
    return this.props.clienteId;
  }
  get clienteNombre(): string | null {
    return this.props.clienteNombre;
  }
  get lectura(): Lectura | null {
    return this.props.lectura;
  }
  get umbral(): Umbral | null {
    return this.props.umbral;
  }

  /**
   * Estado frente a sus umbrales.
   * Sin umbral configurado el sensor no puede juzgarse, aunque tenga lecturas.
   */
  get estado(): EstadoSensor {
    if (!this.props.umbral) return EstadoSensor.sinUmbrales();
    return this.props.umbral.evaluar(this.props.lectura);
  }

  get requiereAtencion(): boolean {
    return this.estado.requiereAtencion;
  }

  get tieneLectura(): boolean {
    return this.props.lectura !== null;
  }

  /** Valor listo para mostrar: `ON`/`OFF` en digitales, número con unidad en analógicos. */
  get valorMostrable(): string {
    const { lectura } = this.props;
    if (!lectura) return '—';
    if (this.props.esDigital) return lectura.esEncendido ? 'ON' : 'OFF';
    const unidad = this.props.simbolo ?? this.props.unidad ?? '';
    const numero = lectura.formatear(this.props.decimales);
    return unidad ? `${numero} ${unidad}` : numero;
  }

  /** Sólo el número, para poder darle a la unidad un estilo distinto en la UI. */
  get valorNumericoMostrable(): string {
    const { lectura } = this.props;
    if (!lectura) return '—';
    if (this.props.esDigital) return lectura.esEncendido ? 'ON' : 'OFF';
    return lectura.formatear(this.props.decimales);
  }

  get unidadMostrable(): string {
    if (this.props.esDigital) return '';
    return this.props.simbolo ?? this.props.unidad ?? '';
  }

  /** Nueva instancia con la lectura actualizada (llegada por tiempo real). */
  conLectura(lectura: Lectura | null): Sensor {
    return new Sensor({ ...this.props, lectura });
  }

  conUmbral(umbral: Umbral | null): Sensor {
    return new Sensor({ ...this.props, umbral });
  }

  /** Coincidencia para el buscador de la interfaz. */
  coincideCon(texto: string): boolean {
    const t = texto.trim().toLowerCase();
    if (!t) return true;
    return [
      this.props.nombre,
      this.props.nodoNombre,
      this.props.areaNombre,
      this.props.clienteNombre,
      this.props.tipoNombre,
      this.props.codigoEnvio,
    ].some((campo) => campo?.toLowerCase().includes(t));
  }
}
